import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import axios from 'axios';
import { Archive, Folder as FolderIcon, FolderOpen, Loader2, RefreshCw } from 'lucide-react';
import TicketBoard from './TicketBoard';
import WinningSlips from './WinningSlips';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/sports-betting`;
const usd = (n) => `${n < 0 ? '-' : ''}$${Math.abs(Number(n || 0)).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const Stat = ({ label, value, tone = 'text-white', testId }) => (
  <div className="rounded-lg border border-zinc-800 bg-zinc-900/70 px-3 py-2" data-testid={testId}>
    <p className="text-[10px] uppercase tracking-wider text-zinc-500">{label}</p>
    <p className={`font-mono text-base font-bold ${tone}`}>{value}</p>
  </div>
);

const Folder = ({ id, label, n, active, onClick, tone }) => (
  <button type="button" onClick={onClick} data-testid={`settled-folder-${id}`}
    className={`flex-1 inline-flex items-center justify-center gap-2 rounded-xl border px-4 py-3 text-sm font-bold transition-colors ${active ? tone : 'border-zinc-800 bg-zinc-900/60 text-zinc-400 hover:text-white'}`}>
    {active ? <FolderOpen className="w-4 h-4" /> : <FolderIcon className="w-4 h-4" />}{label} <span className="font-mono">({n})</span>
  </button>
);

export default function ArchiveView({ onDelete, onArchive, onSettle, reloadKey }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [folder, setFolder] = useState('won');
  const load = useCallback(() => axios.get(`${API}/parlays`, { params: { archived: true } }).then((r) => setData(r.data)).catch(() => setData({ parlays: [], stats: {} })), []);
  useEffect(() => { load(); }, [load, reloadKey]);
  const recheck = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/parlays/recover-and-grade`);
      toast.success('Re-checking every ticket against ESPN. Results fill in over the next minute');
      setTimeout(() => { load(); setBusy(false); }, 25000);
    } catch (e) { toast.error(e.response?.data?.detail || 'Re-check failed'); setBusy(false); }
  };
  if (!data) return <div className="flex justify-center py-10"><Loader2 className="w-5 h-5 animate-spin text-zinc-500" /></div>;
  const all = data.parlays || [];
  const won = all.filter((p) => p.status === 'won');
  const lost = all.filter((p) => p.status === 'lost');
  const grading = all.filter((p) => ['pending', 'live', 'needs_settlement'].includes(p.status)).length;
  const refunded = all.length - won.length - lost.length - grading;
  const list = folder === 'won' ? won : lost;
  const stake = (arr) => arr.reduce((a, p) => a + Number(p.stake || 0), 0);
  const paid = won.reduce((a, p) => a + Number(p.potential_payout || 0), 0);
  const net = paid - stake(won) - stake(lost);
  return (
    <div className="space-y-3" data-testid="archive-view">
      <WinningSlips onSaved={() => { setFolder('won'); load(); }} />
      <div className="flex flex-wrap items-center gap-2">
        <p className="text-sm font-bold text-white" data-testid="settled-title">Settled</p>
        <p className="text-[11px] text-zinc-500">Every finished and deleted ticket, filed under Won or Lost. Unfinished ones are graded automatically and land here when their games end.</p>
        <button type="button" onClick={recheck} disabled={busy} data-testid="archive-recheck"
          className="ml-auto inline-flex items-center gap-1 rounded-md border border-emerald-500/50 px-2.5 py-1 text-[11px] font-bold text-emerald-300 hover:bg-emerald-500/10 disabled:opacity-50">
          <RefreshCw className={`w-3 h-3 ${busy ? 'animate-spin' : ''}`} />{busy ? 'Checking…' : 'Re-check all results'}
        </button>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <Stat label="Win %" value={won.length + lost.length ? `${Math.round((won.length / (won.length + lost.length)) * 100)}%` : '—'} tone="text-amber-300" testId="archive-win-pct" />
        <Stat label="Wagered" value={usd(stake(won) + stake(lost))} testId="archive-wagered" />
        <Stat label="Paid out" value={usd(paid)} tone="text-emerald-300" testId="archive-paid" />
        <Stat label="Net profit" value={usd(net)} tone={net >= 0 ? 'text-emerald-300' : 'text-red-400'} testId="archive-net" />
      </div>
      <div className="flex gap-2">
        <Folder id="won" label="Won" n={won.length} active={folder === 'won'} onClick={() => setFolder('won')} tone="border-emerald-500/60 bg-emerald-950/40 text-emerald-300" />
        <Folder id="lost" label="Lost" n={lost.length} active={folder === 'lost'} onClick={() => setFolder('lost')} tone="border-red-500/60 bg-red-950/40 text-red-300" />
      </div>
      {grading > 0 && <p className="text-[11px] text-amber-300" data-testid="settled-grading">{grading} unfinished ticket{grading > 1 ? 's are' : ' is'} still being graded — {grading > 1 ? 'they move' : 'it moves'} into Won or Lost automatically.</p>}
      {refunded > 0 && <p className="text-[11px] text-zinc-500" data-testid="settled-refunded">{refunded} ticket{refunded > 1 ? 's were' : ' was'} a push or voided (stake refunded), so not in Won or Lost.</p>}
      <p className="text-xs text-zinc-400" data-testid={`settled-folder-summary-${folder}`}>
        {folder === 'won' ? `${won.length} won · wagered ${usd(stake(won))} · paid ${usd(paid)}` : `${lost.length} lost · wagered ${usd(stake(lost))}`}
      </p>
      {list.length === 0 ? (
        <div className="rounded-xl border border-dashed border-zinc-800 p-10 text-center" data-testid="settled-empty">
          <Archive className="w-8 h-8 text-zinc-700 mx-auto mb-2" />
          <p className="text-sm text-zinc-400">No {folder} tickets yet.</p>
        </div>
      ) : (
        <TicketBoard tickets={list} onDelete={onDelete} onArchive={onArchive} onSettle={onSettle} onChanged={load} archived />
      )}
    </div>
  );
}
